import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import ProductModal from '../productmodal/ProductModal';
import { FirebaseService } from '../../services/FirebaseService';
import { NotificationContext } from '../../components/context/Notification';
import { UserContext } from '../../components/context/User';
import { collections } from '../../constants';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import "./Home.css";

const predefinedOrder = ['Deck', 'Porch', 'Carport', 'Garage'];

const sortProducts = (products) => {
  return products.sort((a, b) => {
    const indexA = predefinedOrder.indexOf(a.name);
    const indexB = predefinedOrder.indexOf(b.name);
    return indexA - indexB;
  });
};

function Home() {
  const { user, setUser } = useContext(UserContext);
  const { setNotification } = useContext(NotificationContext);
  const [products, setProducts] = useState([]);
  const [displayImage, setDisplayImage] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imageMap, setImageMap] = useState({});
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(false);

  const fire = useMemo(() => new FirebaseService(setUser, setNotification), [setUser, setNotification]);

  const fetchData = useCallback(async (map) => {
    let list = [];
    let displayMap = displayImage;
    let imageUrlMap = images;

    const cachedImages = JSON.parse(localStorage.getItem('cachedImages')) || {};

    if (map && Object.keys(map)?.length) {
      for (let key of Object.keys(map)) {
        let sorted = JSON.parse(JSON.stringify(map[key].images)).sort();

        if (map[key].images.length &&
          (!images[key] || (images[key] && JSON.stringify(sorted) !== JSON.stringify(Object.keys(images[key]))))) {
          if (cachedImages[key]) {
            imageUrlMap[key] = cachedImages[key];
            displayMap[key] = cachedImages[key][map[key].images[0]];
          } else {
            let keyMap = await fire.getAllStoredImages(`Home/${key}`);
            if (keyMap) {
              cachedImages[key] = keyMap;
              localStorage.setItem('cachedImages', JSON.stringify(cachedImages));

              imageUrlMap[key] = keyMap;
              displayMap[key] = keyMap[map[key].images[0]];
            }
          }
        } else {
          displayMap[key] = images[key][map[key].images[0]];
        }
        list.push(map[key]);
      }
    }

    setDisplayImage(displayMap);
    setProducts(list);
    setImages(imageUrlMap);
    setLoading(false);
  }, [displayImage, images, fire]);

  useEffect(() => {
    if (user && !Object.keys(imageMap)?.length) {
      setLoading(true);
      fire.getCollectionDocs(collections.photos).then(result => {
        if (result) {
          setImageMap(result);
          fetchData(result);
        }
      });
    }
  }, [user, imageMap, fetchData, fire]);

  const openModal = async (product) => {
    setSelectedProduct(product);
  };

  const closeModal = (save, id, newList) => {
    setSelectedProduct(null);
    if (save) {
      setLoading(true);
      let map = imageMap;
      map[id].images = newList;
      updatePhotos(id, map, true);
    }
  }

  function addImage(calc, image, name) {
    setLoading(true);
    fire.addImage(image, `Home/${calc}/${name}`).then(() => {
      let map = imageMap;
      map[calc].images.push(name);
      updatePhotos(calc, map);
    });
  }

  function deleteImage(calc, name) {
    setLoading(true);
    fire.deleteImage(`Home/${calc}/${name}`).then(() => {
      let map = imageMap;
      map[calc].images = map[calc].images.filter(i => i !== name);
      updatePhotos(calc, map);
    });
  }

  function updateList(id, newList) {
    let map = imageMap;
    map[id].images = newList;
    setImageMap(map);
  }

  function updatePhotos(id, newMap, isClosed) {
    fire.updateDocument(collections.photos, id, newMap[id]).then(() => {
      setImageMap(newMap);
      fetchData(newMap);
      if (!isClosed) {
        openModal(products.find(i => i.id === selectedProduct.id));
      }
    });
  }

  const sortedProducts = useMemo(() => sortProducts(products), [products]);

  return (
    <div>
      <div className="products">
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="product-item">
              <Skeleton height={150} />
              <Skeleton width={100} />
            </div>
          ))
        ) : (
          sortedProducts.length > 0 && sortedProducts.map(product => (
            <div key={product.id} onClick={() => openModal(product)} className="product-item">
              <img src={displayImage[product.id]} alt={product.name} loading="lazy" />
              <h3 className="product-title">{product.name}</h3>
            </div>
          ))
        )}
      </div>

      {selectedProduct && images[selectedProduct.id] && (
        <ProductModal
          product={selectedProduct}
          images={images[selectedProduct.id]} // Pass images directly
          onClose={closeModal}
          isAdmin={user && !user.isAnonymous && user.emailVerified}
          deletePhoto={deleteImage}
          addPhoto={addImage}
          onDrag={updateList}
        />
      )}
    </div>
  );
}

export default Home;
